import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '../../shared/services/config.service';

/** TT-258. Email, phone and what is missing, for one installer on the Connections list. */
export interface ConnectionContact {
  installerTeamLeadId: string;
  email: string | null;
  phoneNumber: string | null;
  insuranceExpiration: string | null;
  /** "Phone", "W9", "COI", "COI expired". */
  missing: string[];
}

export type FlagSeverity = 'MINOR' | 'MEDIUM' | 'CRITICAL';

/** TT-259. A company's own flag on an installer who has worked for it. */
export interface InstallerFlag {
  id: number;
  installerTeamLeadId: string;
  severity: FlagSeverity;
  issue: string;
  createdOn: string;
  createdById: string;
  createdByName: string;
}

/** TT-260 item 2. How the installers a work order went to have responded. */
export interface WorkOrderResponseSummary {
  matched: number;
  seen: number;
  responded: number;
  declined: number;
}

/** TT-260 item 10. A work order's value with changes, what is invoiced and what is left. */
export interface WorkOrderTotals {
  totalWithChanges: number;
  invoicedToDate: number;
  remaining: number;
  pendingChanges: number;
}

/**
 * Company-side reads and writes added for John's tickets (TT-258, TT-259,
 * TT-260). Plain calls rather than store entities: each is read-only or scoped
 * to one screen, and none needs to live in the global state.
 */
@Injectable({ providedIn: 'root' })
export class CompanyInsightsService {
  constructor(private readonly http: HttpClient) {}

  contactDetails(): Observable<ConnectionContact[]> {
    return this.http.get<ConnectionContact[]>(`${ConfigService.getHost()}/my-network/contact-details`);
  }

  flags(installerId?: string): Observable<InstallerFlag[]> {
    const params = installerId ? new HttpParams().set('installerId', installerId) : undefined;
    return this.http.get<InstallerFlag[]>(`${ConfigService.getHost()}/installer-flags`, { params });
  }

  addFlag(installerTeamLeadId: string, severity: FlagSeverity, issue: string): Observable<InstallerFlag> {
    return this.http.post<InstallerFlag>(`${ConfigService.getHost()}/installer-flags`, {
      installerTeamLeadId,
      severity,
      issue,
    });
  }

  removeFlag(id: number): Observable<void> {
    return this.http.delete<void>(`${ConfigService.getHost()}/installer-flags/${id}`);
  }

  responseSummary(workOrderId: string): Observable<WorkOrderResponseSummary> {
    return this.http.get<WorkOrderResponseSummary>(
      `${ConfigService.getHost()}/work-orders/${workOrderId}/response-summary`,
    );
  }

  totals(workOrderId: string): Observable<WorkOrderTotals> {
    return this.http.get<WorkOrderTotals>(`${ConfigService.getHost()}/work-orders/${workOrderId}/totals`);
  }
}

export const FLAG_SEVERITY_LABELS: Record<FlagSeverity, string> = {
  MINOR: 'Minor',
  MEDIUM: 'Medium',
  CRITICAL: 'Critical',
};

/** Yellow, orange and red, as TT-259 asks. */
export const FLAG_SEVERITY_COLORS: Record<FlagSeverity, string> = {
  MINOR: '#f5c400',
  MEDIUM: '#ff8a00',
  CRITICAL: '#e0271a',
};

const SEVERITY_ORDER: FlagSeverity[] = ['MINOR', 'MEDIUM', 'CRITICAL'];

/** The most serious flag decides the colour shown next to the name. */
export const topSeverity = (flags: InstallerFlag[]): FlagSeverity | undefined =>
  flags.reduce<FlagSeverity | undefined>(
    (top, flag) => (!top || SEVERITY_ORDER.indexOf(flag.severity) > SEVERITY_ORDER.indexOf(top) ? flag.severity : top),
    undefined,
  );

/** What a hover shows: every flag, most recent first. */
export const flagSummary = (flags: InstallerFlag[]): string =>
  flags
    .map(
      flag =>
        `${FLAG_SEVERITY_LABELS[flag.severity]}: ${flag.issue} (${flag.createdByName || 'a colleague'}, ${new Date(
          flag.createdOn,
        ).toLocaleDateString()})`,
    )
    .join('\n');

/** (316) 555-0100 for a US number, anything else as stored. */
export const formatPhone = (value?: string | null): string => {
  const digits = (value ?? '').replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return local.length === 10 ? `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}` : value ?? '';
};
